import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../state/auth';
import { TimeClockPage } from './TimeClockPage';
import { DashboardPage } from './DashboardPage';
import { HistoryPage } from './HistoryPage';
import { MessagesPage } from './MessagesPage';
import { SettingsPage } from './SettingsPage';
import { MonthlyReportPage } from './MonthlyReportPage';

function Icon({
  name
}: {
  name: 'clock' | 'dash' | 'hist' | 'report' | 'msg' | 'settings';
}) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg'
  } as const;

  const strokeProps = {
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const
  };

  if (name === 'clock') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" {...strokeProps} />
        <path d="M12 7v6l4 2" {...strokeProps} />
      </svg>
    );
  }

  if (name === 'dash') {
    return (
      <svg {...common}>
        <path d="M4 13h6V4H4v9zM14 20h6V11h-6v9zM14 4v5h6V4h-6zM4 20h6v-5H4v5z" {...strokeProps} />
      </svg>
    );
  }

  if (name === 'hist') {
    return (
      <svg {...common}>
        <path d="M21 12a9 9 0 1 1-3-6.7" {...strokeProps} />
        <path d="M21 3v6h-6" {...strokeProps} />
        <path d="M12 7v5l3 2" {...strokeProps} />
      </svg>
    );
  }

  if (name === 'report') {
    return (
      <svg {...common}>
        <path d="M7 3h10a2 2 0 0 1 2 2v16l-3-2-3 2-3-2-3 2-3-2V5a2 2 0 0 1 2-2z" {...strokeProps} />
        <path d="M8 8h8" {...strokeProps} />
        <path d="M8 12h8" {...strokeProps} />
        <path d="M8 16h5" {...strokeProps} />
      </svg>
    );
  }

  if (name === 'msg') {
    return (
      <svg {...common}>
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8z" {...strokeProps} />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" {...strokeProps} />
      <path d="M19.4 15a1.8 1.8 0 0 0 .36 1.98l.06.06a2.2 2.2 0 0 1-1.56 3.76 2.2 2.2 0 0 1-1.56-.64l-.06-.06a1.8 1.8 0 0 0-1.98-.36 1.8 1.8 0 0 0-1.08 1.65V21a2.2 2.2 0 0 1-4.4 0v-.09a1.8 1.8 0 0 0-1.08-1.65 1.8 1.8 0 0 0-1.98.36l-.06.06a2.2 2.2 0 0 1-3.12 0 2.2 2.2 0 0 1 0-3.12l.06-.06A1.8 1.8 0 0 0 4.6 15a1.8 1.8 0 0 0-1.65-1.08H2.9a2.2 2.2 0 0 1 0-4.4h.09A1.8 1.8 0 0 0 4.6 8.4a1.8 1.8 0 0 0-.36-1.98l-.06-.06a2.2 2.2 0 0 1 3.12-3.12l.06.06A1.8 1.8 0 0 0 9.3 2.94h.09a2.2 2.2 0 0 1 4.4 0h.09a1.8 1.8 0 0 0 1.65 1.08 1.8 1.8 0 0 0 1.98-.36l.06-.06a2.2 2.2 0 0 1 3.12 3.12l-.06.06A1.8 1.8 0 0 0 19.4 8.4a1.8 1.8 0 0 0 1.65 1.08h.09a2.2 2.2 0 0 1 0 4.4h-.09A1.8 1.8 0 0 0 19.4 15z" {...strokeProps} />
    </svg>
  );
}

function NavItem({
  to,
  label,
  active,
  icon
}: {
  to: string;
  label: string;
  active: boolean;
  icon: React.ReactNode;
}) {
  return (
    <Link to={to} className={`mobileNavItem ${active ? 'mobileNavItemActive' : ''}`}>
      <span className="navIcon" aria-hidden="true">
        {icon}
      </span>

      <span
        style={{
          fontWeight: 900,
          fontSize: 10,
          lineHeight: 1.1,
          textAlign: 'center'
        }}
      >
        {label}
      </span>
    </Link>
  );
}

export function EmployeeLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const path = loc.pathname;

  const [isWide, setIsWide] = useState(() => window.innerWidth >= 1024);

  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= 1024);

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [showGuide, setShowGuide] = useState(() => !localStorage.getItem('guide_done'));

  const steps = useMemo(() => ([
    {
      title: 'Posizione',
      body: 'Per timbrare devi consentire la geolocalizzazione.'
    },
    {
      title: 'Notifiche',
      body: 'Attiva le notifiche push per pause lunghe e straordinari.'
    },
    {
      title: 'Aggiungi alla Home',
      body: 'Suggerito: aggiungi l’app alla home per esperienza “app”.'
    }
  ]), []);

  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!showGuide) return;
  }, [showGuide]);

  function closeGuide() {
    localStorage.setItem('guide_done', '1');
    setShowGuide(false);
  }

  if (isWide) {
    return (
      <div className="gateScreen">
        <div className="card gateCard">
          <div className="h2" style={{ marginTop: 0 }}>
            Accesso Dipendente
          </div>

          <div className="small">
            Questa sezione è ottimizzata solo per smartphone (touch + barra inferiore).
          </div>

          <div className="small" style={{ marginTop: 10 }}>
            Apri il link dal telefono per l’esperienza migliore.
          </div>

          <div style={{ marginTop: 16 }} className="poweredBy">
            Powered by <b>Mattia Donizetti</b>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employeeApp">
      <header className="employeeHeader">
        <div className="employeeHeaderInner employeeNarrow">
          <div className="employeeBrand">
            <div className="employeeBrandRow">
              <img
                className="h-15 sm:h-15 md:h-30"
                src="/brand/infinityWifi.png"
                alt="INFINITY WIFI"
              />
            </div>
          </div>

          <button className="btn btnGhost" onClick={() => logout()}>
            Esci
          </button>
        </div>
      </header>

      <main className="container employeeNarrow" style={{ paddingBottom: 112 }}>
        <Routes>
          <Route path="/" element={<Navigate to="/timbratura" replace />} />
          <Route path="/timbratura" element={<TimeClockPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/resoconto-mese" element={<MonthlyReportPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>

        <div style={{ marginTop: 18, textAlign: 'center' }} className="poweredBy">
          Powered by <b>Mattia Donizetti</b>
        </div>
      </main>

      <nav className="mobileNav">
        <div
          className="mobileNavGrid employeeNarrow"
          style={{
            gridTemplateColumns: 'repeat(6, minmax(0, 1fr))'
          }}
        >
          <NavItem
            to="/timbratura"
            label="Timbra"
            icon={<Icon name="clock" />}
            active={path.startsWith('/timbratura') || path === '/'}
          />

          <NavItem
            to="/dashboard"
            label="Home"
            icon={<Icon name="dash" />}
            active={path.startsWith('/dashboard')}
          />

          <NavItem
            to="/history"
            label="Storico"
            icon={<Icon name="hist" />}
            active={path.startsWith('/history')}
          />

          <NavItem
            to="/resoconto-mese"
            label="Resoc."
            icon={<Icon name="report" />}
            active={path.startsWith('/resoconto-mese')}
          />

          <NavItem
            to="/messages"
            label="Messaggi"
            icon={<Icon name="msg" />}
            active={path.startsWith('/messages')}
          />

          <NavItem
            to="/settings"
            label="Impost."
            icon={<Icon name="settings" />}
            active={path.startsWith('/settings')}
          />
        </div>
      </nav>

      {showGuide ? (
        <div className="modalOverlay" onClick={closeGuide}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="h3" style={{ marginTop: 0 }}>
              Prima volta qui
            </div>

            <div className="p">
              {steps[step].title}
            </div>

            <div className="small">
              {steps[step].body}
            </div>

            <div className="spread" style={{ marginTop: 16 }}>
              <button className="btn btnSecondary" onClick={closeGuide}>
                Chiudi
              </button>

              <button
                className="btn btnPrimary"
                onClick={() => {
                  if (step >= steps.length - 1) closeGuide();
                  else setStep(step + 1);
                }}
              >
                {step >= steps.length - 1 ? 'Fatto' : 'Avanti'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}